import type { PaletteOptions } from "@mui/material/styles";

/** MUI breakpoint values from the Baana C2B design system. */
export const breakpoints = {
  values: {
    xs: 0,
    sm: 600,
    md: 900,
    lg: 1366,
    xl: 1920
  }
} as const;

/** Base spacing unit (px). MUI multiplies this by the spacing factor. */
export const spacingUnit = 8;

/**
 * T-shirt spacing tokens as MUI spacing factors.
 * Usage: `theme.spacing(spacing.md)` → 16px
 */
export const spacing = {
  xs: 0.5,
  sm: 1,
  md: 2,
  lg: 3,
  xl: 4,
  "2xl": 6,
  "3xl": 8
} as const;

export type SpacingToken = keyof typeof spacing;

/**
 * Spacing scale in raw pixels (8px grid).
 * Prefer `theme.spacing(spacing.md)` in MUI `sx`; use `spacingPx` when you need
 * a number (e.g. canvas math, non-MUI styles).
 */
export const spacingPx = {
  unit: spacingUnit,
  xs: spacingUnit * spacing.xs,
  sm: spacingUnit * spacing.sm,
  md: spacingUnit * spacing.md,
  lg: spacingUnit * spacing.lg,
  xl: spacingUnit * spacing.xl,
  "2xl": spacingUnit * spacing["2xl"],
  "3xl": spacingUnit * spacing["3xl"]
} as const;

/**
 * Border-radius scale as CSS lengths.
 * Use strings (not bare numbers) in MUI `sx`: a number is multiplied by
 * `theme.shape.borderRadius`, so `borderRadius: 12` ≠ 12px.
 * Usage: `borderRadius: radius.md`
 */
export const radius = {
  none: "0",
  xs: "4px",
  sm: "8px",
  md: "12px",
  lg: "16px",
  xl: "20px",
  pill: "999px",
  /** Hero title card and similar marketing shapes. */
  hero: "60px"
} as const;

export type RadiusToken = keyof typeof radius;

export interface SpacingTokenDefinition {
  token: SpacingToken;
  factor: number;
  px: number;
  rem: string;
  use: string;
}

/** Spacing token metadata for docs and Storybook. */
export const spacingTokenDefinitions: SpacingTokenDefinition[] = [
  {
    token: "xs",
    factor: spacing.xs,
    px: spacingPx.xs,
    rem: "0.25rem",
    use: "Icon gaps, tight inline spacing"
  },
  {
    token: "sm",
    factor: spacing.sm,
    px: spacingPx.sm,
    rem: "0.5rem",
    use: "Compact stacks, small padding"
  },
  {
    token: "md",
    factor: spacing.md,
    px: spacingPx.md,
    rem: "1rem",
    use: "Default padding, form field gaps"
  },
  {
    token: "lg",
    factor: spacing.lg,
    px: spacingPx.lg,
    rem: "1.5rem",
    use: "Card padding, section inner spacing"
  },
  {
    token: "xl",
    factor: spacing.xl,
    px: spacingPx.xl,
    rem: "2rem",
    use: "Section gaps, larger containers"
  },
  {
    token: "2xl",
    factor: spacing["2xl"],
    px: spacingPx["2xl"],
    rem: "3rem",
    use: "Major section separation"
  },
  {
    token: "3xl",
    factor: spacing["3xl"],
    px: spacingPx["3xl"],
    rem: "4rem",
    use: "Page-level vertical rhythm"
  }
];

export {
  createHeadingBreakpointTypography,
  fontFamily,
  fontWeights,
  formatTypographyTransform,
  typography,
  typographyComponentTokenNames,
  typographyHeadingBreakpointTokens,
  typographyHeadingBreakpointVariants,
  typographyTokenNames,
  typographyTokens,
  typographyTransformLabels,
  type TypographyFontWeight,
  type TypographyHeadingBreakpointVariant,
  type TypographyTextTransform,
  type TypographyTokenDefinition,
  type TypographyTokenName
} from "@/src/theme/customizations/typography";

export interface SemanticColorTokens {
  main: string;
  dark: string;
  light: string;
  contrastText: string;
  hover4p?: string;
  hover8p?: string;
  selected8p?: string;
  selected16p?: string;
  focus12p: string;
  focus30p: string;
  ripple30p: [string, string];
  enabled50p: string;
  alertContent160p?: string;
  alertBackground190p?: string;
  alertContext900?: string;
  alertBackground100s?: string;
}

/**
 * Semantic color ready for app use: Figma keys plus resolved interaction keys.
 * Prefer `theme.palette.semantic.*.hover|selected|focus|alertBg|alertText`
 * over raw hover4p/selected8p fallbacks.
 */
export interface ResolvedSemanticColor extends SemanticColorTokens {
  /** Resolved hover overlay (hover4p ?? hover8p). */
  hover: string;
  /** Resolved selected overlay (selected8p ?? selected16p ?? hover). */
  selected: string;
  /** Resolved focus overlay (focus30p). */
  focus: string;
  /** Resolved alert background (alertBackground190p ?? alertBackground100s). */
  alertBg: string;
  /** Resolved alert text (alertContent160p ?? alertContext900). */
  alertText: string;
}

export interface ModeColorTokens {
  text: {
    primary: string;
    secondary: string;
    disabled: string;
  };
  primary: SemanticColorTokens;
  secondary: SemanticColorTokens;
  error: SemanticColorTokens;
  warning: SemanticColorTokens;
  info: SemanticColorTokens;
  success: SemanticColorTokens;
  action: {
    active: string;
    hover: string;
    selected: string;
    disabled: string;
    disabledBackground: string;
    focus: string;
  };
  background: {
    default: string;
    paper: string;
    elevation1?: string;
    elevation2?: string;
    elevation3?: string;
    elevation4?: string;
    elevation6?: string;
    elevation8?: string;
    elevation12?: string;
    elevation16?: string;
    elevation24?: string;
  };
  divider: string;
  misc: {
    coldBackground?: string;
    outlineBorder: string;
    standardInputLine: string;
    backdropOverlay?: string;
    ratingActive: string;
    timelineBackground?: string;
    secondaryBrandColor?: string;
    tertiaryBrandColor?: string;
    katsastusBrandColor?: string;
    visitedLinkColor?: string;
    /** Dark-mode unvisited text links in articles and forms. */
    linkColor?: string;
    /** Dark-mode visited text links in articles and forms. */
    darkVisitedLinkColor?: string;
    snackbarBackground?: string;
    almaLoginMain?: string;
    almaLoginHover?: string;
    almaLoginFocusRing?: string;
  };
}

/** Light mode color tokens from docs/color-theme.md (MUI for Baana C2B Figma). */
export const lightColors: ModeColorTokens = {
  text: {
    primary: "#000000DD",
    secondary: "#00000099",
    disabled: "#00000061"
  },
  primary: {
    main: "#CC3300",
    dark: "#9E2A03",
    light: "#DD4F1F",
    contrastText: "#FFFFFF",
    hover4p: "#CC33000A",
    selected8p: "#CC330014",
    focus12p: "#CC33001F",
    focus30p: "#CC33004D",
    ripple30p: ["#CC33004D", "#FFFFFF00"],
    enabled50p: "#CC330080"
  },
  secondary: {
    main: "#333333",
    dark: "#202020",
    light: "#333333",
    contrastText: "#FFFFFF",
    hover4p: "#3333330A",
    selected8p: "#33333314",
    focus12p: "#3333331F",
    focus30p: "#3333334D",
    ripple30p: ["#3333334D", "#FFFFFF00"],
    enabled50p: "#33333380"
  },
  error: {
    main: "#BB2233",
    dark: "#A31020",
    light: "#EA5666",
    contrastText: "#FFFFFF",
    hover4p: "#BB22330A",
    focus12p: "#BB22331F",
    focus30p: "#BB22334D",
    ripple30p: ["#BB22334D", "#FFFFFF00"],
    enabled50p: "#D32F2F80",
    alertContent160p: "#4B0E14",
    alertBackground190p: "#F8E9EB"
  },
  warning: {
    main: "#D95B00",
    dark: "#C15304",
    light: "#F39653",
    contrastText: "#FFFFFF",
    hover4p: "#D95B000A",
    focus12p: "#D95B001F",
    focus30p: "#D95B004D",
    ripple30p: ["#D95B004D", "#FFFFFF00"],
    enabled50p: "#D95B0080",
    alertContent160p: "#572400",
    alertBackground190p: "#F7DECC"
  },
  info: {
    main: "#0866C9",
    dark: "#0557AD",
    light: "#529CEA",
    contrastText: "#FFFFFF",
    hover4p: "#0866C90A",
    selected8p: "#0866C914",
    focus12p: "#0866C91F",
    focus30p: "#0866C94D",
    ripple30p: ["#0866C94D", "#FFFFFF00"],
    enabled50p: "#0866C980",
    alertContext900: "#032950",
    alertBackground100s: "#CEE0F4"
  },
  success: {
    main: "#538A01",
    dark: "#436B08",
    light: "#82B13B",
    contrastText: "#FFFFFF",
    hover4p: "#538A010A",
    selected8p: "#538A0114",
    focus12p: "#538A011F",
    focus30p: "#538A014D",
    ripple30p: ["#538A014D", "#FFFFFF00"],
    enabled50p: "#538A0180",
    alertContext900: "#213700",
    alertBackground100s: "#DAE4C7"
  },
  action: {
    active: "#0000008A",
    hover: "#0000000A",
    selected: "#00000014",
    disabled: "#00000042",
    disabledBackground: "#0000001F",
    focus: "#0000001F"
  },
  background: {
    default: "#FFFFFF",
    paper: "#FFFFFF"
  },
  divider: "#0000001F",
  misc: {
    coldBackground: "#EBEBEB",
    outlineBorder: "#0000003B",
    standardInputLine: "#0000006B",
    backdropOverlay: "#00000080",
    ratingActive: "#FFB400",
    timelineBackground: "#F5F5F5",
    secondaryBrandColor: "#F15A24",
    tertiaryBrandColor: "#F9F2ED",
    katsastusBrandColor: "#10069F",
    visitedLinkColor: "#551A8B",
    snackbarBackground: "#323232",
    almaLoginMain: "#992687",
    almaLoginHover: "#7d1f6f",
    almaLoginFocusRing: "#d2b9dc"
  }
};

/** Hero section gradient stops — brand marketing exception (not on MUI palette). */
export const heroGradientColors = {
  accent: "#FF6A2E",
  deep: "#B82A08",
  mid: "#E44A0F",
  base: "#CC3300",
  card: "#D63900"
} as const;

/**
 * Home page hero dark section — marketing exception (not on MUI palette).
 * Backgrounds differ from light theme tokens; accents reuse primary/contrastText.
 */
export const homePageHeroColors = {
  meshBackground: "#1A0905",
  meshDeepBrown: "#0F0705",
  meshMidBrown: "#3A0F06",
  meshTeal: "#121A1C",
  meshTitleAccent: "#A82806",
  meshTitleAccentBright: "#E83808",
  meshTitleAccentHighlight: "#FF5520",
  heroBackground: "#160900",
  instructionsBackground: "#141210",
  instructionsStepNumberColor: "#B22D00",
  instructionsStepBadgeBackground: "#B22D0029",
  selectorActiveBackground: "#141210",
  selectorInactiveBorder: "#B22D0029",
  /** Dark card background for VehicleStartAuctionCard. */
  auctionCardDarkBackground: "#1B120D",
  /**
   * Dark mode semi-transparent white border (33 % opacity).
   * Used by FAQ list section and Katsastus banner card.
   * Intentionally stronger than darkColors.misc.outlineBorder (#FFFFFF3B / 23 %).
   */
  cardDarkBorder: "#FFFFFF54",
  /** Primary brand glow at 10 % opacity — dark card box-shadow. */
  brandGlowDark: "#B22D001A"
} as const;

/** Dark mode color tokens from docs/color-theme.md (MUI for Baana C2B Figma). */
export const darkColors: ModeColorTokens = {
  text: {
    primary: "#F2EFEB",
    secondary: "#C2BFBC",
    disabled: "#928F8D"
  },
  primary: {
    main: "#B22D00",
    dark: "#932500",
    light: "#CB4A21",
    contrastText: "#000000",
    hover8p: "#B22D0014",
    selected16p: "#B22D0029",
    focus12p: "#B22D001F",
    focus30p: "#B22D004D",
    ripple30p: ["#B22D004D", "#00000000"],
    enabled50p: "#B22D0080"
  },
  secondary: {
    main: "#FAFAFA",
    dark: "#DDDDDD",
    light: "#FFFFFF",
    contrastText: "#000000",
    hover8p: "#FAFAFA14",
    selected16p: "#FAFAFA29",
    focus12p: "#FAFAFA1F",
    focus30p: "#FAFAFA4D",
    ripple30p: ["#FAFAFA4D", "#00000000"],
    enabled50p: "#FAFAFA80"
  },
  error: {
    main: "#FA818E",
    dark: "#79131E",
    light: "#FD9CA6",
    contrastText: "#000000",
    hover8p: "#FA818E14",
    focus12p: "#FA818E1F",
    focus30p: "#FA818E4D",
    ripple30p: ["#FA818E4D", "#00000000"],
    enabled50p: "#FA818E80",
    alertContent160p: "#FDCDD2",
    alertBackground190p: "#321A1C"
  },
  warning: {
    main: "#FFC861",
    dark: "#CA8708",
    light: "#FFD68A",
    contrastText: "#000000",
    hover8p: "#FFC86114",
    focus12p: "#FFC8611F",
    focus30p: "#FFC8614D",
    ripple30p: ["#FFC8614D", "#00000000"],
    enabled50p: "#FFC86180",
    alertContent160p: "#FFE9C0",
    alertBackground190p: "#332813"
  },
  info: {
    main: "#83ACFF",
    dark: "#4D82EF",
    light: "#ADC8FF",
    contrastText: "#000000",
    hover8p: "#83ACFF14",
    focus12p: "#83ACFF1F",
    focus30p: "#83ACFF4D",
    ripple30p: ["#83ACFF4D", "#00000000"],
    enabled50p: "#83ACFF80",
    alertContent160p: "#CDDEFF",
    alertBackground190p: "#1A2233"
  },
  success: {
    main: "#9CCD52",
    dark: "#3E600C",
    light: "#C0E18F",
    contrastText: "#000000",
    hover8p: "#9CCD5214",
    focus12p: "#9CCD521F",
    focus30p: "#9CCD524D",
    ripple30p: ["#9CCD524D", "#00000000"],
    enabled50p: "#9CCD5280",
    alertContent160p: "#D7EBBA",
    alertBackground190p: "#1F2910"
  },
  action: {
    active: "#FFFFFF8F",
    hover: "#FFFFFF14",
    selected: "#FFFFFF29",
    disabled: "#FFFFFF4D",
    disabledBackground: "#FFFFFF1F",
    focus: "#FFFFFF1F"
  },
  background: {
    default: "#141210",
    paper: "#221E1A",
    elevation1: "#2D2925",
    elevation2: "#312E2A",
    elevation3: "#34302C",
    elevation4: "#36322F",
    elevation6: "#3A3733",
    elevation8: "#3D3935",
    elevation12: "#413E3A",
    elevation16: "#43403C",
    elevation24: "#45423F"
  },
  divider: "#FFFFFF1F",
  misc: {
    coldBackground: "#291F1B",
    outlineBorder: "#FFFFFF3B",
    standardInputLine: "#FFFFFF6B",
    backdropOverlay: "#00000080",
    ratingActive: "#FFB400",
    secondaryBrandColor: "#F15A24",
    tertiaryBrandColor: "#160900",
    visitedLinkColor: "#551A8B",
    linkColor: "#DF7752",
    darkVisitedLinkColor: "#BB68DF",
    snackbarBackground: "#E2E2E2",
    almaLoginMain: "#992687",
    almaLoginHover: "#7d1f6f",
    almaLoginFocusRing: "#d2b9dc"
  }
};

const mapSemanticPalette = (colors: SemanticColorTokens) => ({
  main: colors.main,
  light: colors.light,
  dark: colors.dark,
  contrastText: colors.contrastText
});

/** Resolves mode-specific Figma keys into stable interaction tokens. */
export const normalizeSemanticColor = (
  colors: SemanticColorTokens,
  fallbacks: { hover: string; selected: string; focus: string; alertBg: string }
): ResolvedSemanticColor => {
  const hover = colors.hover4p ?? colors.hover8p ?? fallbacks.hover;
  const selected =
    colors.selected8p ?? colors.selected16p ?? hover ?? fallbacks.selected;

  return {
    ...colors,
    hover,
    selected,
    focus: colors.focus30p ?? fallbacks.focus,
    alertBg:
      colors.alertBackground190p ??
      colors.alertBackground100s ??
      fallbacks.alertBg,
    alertText: colors.alertContent160p ?? colors.alertContext900 ?? colors.dark
  };
};

/** Maps design tokens to an MUI palette for the given color mode. */
export const createPaletteFromTokens = (
  colors: ModeColorTokens,
  mode: "light" | "dark"
): PaletteOptions => {
  const interactionFallbacks = {
    hover: colors.action.hover,
    selected: colors.action.selected,
    focus: colors.action.focus,
    alertBg: colors.background.paper
  };

  return {
    mode,
    text: colors.text,
    primary: mapSemanticPalette(colors.primary),
    secondary: mapSemanticPalette(colors.secondary),
    error: mapSemanticPalette(colors.error),
    warning: mapSemanticPalette(colors.warning),
    info: mapSemanticPalette(colors.info),
    success: mapSemanticPalette(colors.success),
    action: colors.action,
    background: {
      default: colors.background.default,
      paper: colors.background.paper
    },
    divider: colors.divider,
    brand: {
      secondary: colors.misc.secondaryBrandColor ?? colors.primary.main,
      tertiary: colors.misc.tertiaryBrandColor ?? colors.background.paper,
      katsastus: colors.misc.katsastusBrandColor ?? "#10069F"
    },
    misc: {
      coldBackground: colors.misc.coldBackground ?? colors.background.paper,
      outlineBorder: colors.misc.outlineBorder,
      standardInputLine: colors.misc.standardInputLine,
      backdropOverlay:
        colors.misc.backdropOverlay ?? colors.action.disabledBackground,
      ratingActive: colors.misc.ratingActive,
      timelineBackground:
        colors.misc.timelineBackground ?? colors.background.paper,
      snackbarBackground:
        colors.misc.snackbarBackground ?? colors.background.paper,
      almaLoginMain: colors.misc.almaLoginMain,
      almaLoginHover: colors.misc.almaLoginHover,
      almaLoginFocusRing: colors.misc.almaLoginFocusRing,
      visitedLink: colors.misc.visitedLinkColor,
      link: colors.misc.linkColor,
      darkVisitedLink: colors.misc.darkVisitedLinkColor,
      elevation1: colors.background.elevation1 ?? colors.background.paper,
      elevation2: colors.background.elevation2 ?? colors.background.paper,
      elevation3: colors.background.elevation3 ?? colors.background.paper,
      elevation4: colors.background.elevation4 ?? colors.background.paper,
      elevation6: colors.background.elevation6 ?? colors.background.paper,
      elevation8: colors.background.elevation8 ?? colors.background.paper,
      elevation12: colors.background.elevation12 ?? colors.background.paper,
      elevation16: colors.background.elevation16 ?? colors.background.paper,
      elevation24: colors.background.elevation24 ?? colors.background.paper
    },
    semantic: {
      primary: normalizeSemanticColor(colors.primary, interactionFallbacks),
      secondary: normalizeSemanticColor(colors.secondary, interactionFallbacks),
      error: normalizeSemanticColor(colors.error, interactionFallbacks),
      warning: normalizeSemanticColor(colors.warning, interactionFallbacks),
      info: normalizeSemanticColor(colors.info, interactionFallbacks),
      success: normalizeSemanticColor(colors.success, interactionFallbacks)
    }
  };
};

export const lightPalette = createPaletteFromTokens(lightColors, "light");
export const darkPalette = createPaletteFromTokens(darkColors, "dark");
